import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, ExternalLink, PenLine } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdOrders() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("pending_review");

  const { data: orders } = useQuery({
    queryKey: ["admin-ad-orders", status],
    queryFn: async () => (await api.get(`/admin/ad-orders?status=${status}`)).data,
  });

  const resolve = async (order: any, action: "approve" | "reject" | "request_changes") => {
    let note: string | null = null;
    if (action === "request_changes") {
      note = window.prompt("What needs to change? This note is emailed to the advertiser:");
      if (!note?.trim()) return;
    }
    if (action === "reject") {
      if (!window.confirm(`FINAL rejection of "${order.name}" — the advertiser will be automatically refunded $${order.price_usd} via Stripe. Continue?`)) return;
      note = window.prompt("Optional rejection reason (emailed to the advertiser):") || null;
    }
    try {
      const res = await api.patch(`/admin/ad-orders/${order.id}`, { action, note });
      toast.success(res.data.message);
      queryClient.invalidateQueries({ queryKey: ["admin-ad-orders"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-ad-orders-page">
      <PageHeader title="Ad Orders" sub="Review paid self-serve ad campaigns before they go live"
        actions={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 bg-[#1A1A1A] border-white/10 text-white" data-testid="ad-orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="pending_review">Pending review</SelectItem>
              <SelectItem value="changes_requested">Changes requested</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="draft_unpaid">Unpaid drafts</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        } />

      <Table headers={["Campaign", "Creative", "Placement", "Duration", "Price", "Paid", "Created", "Status", "Actions"]}>
        {(orders || []).map((o: any) => (
          <tr key={o.id} data-testid={`ad-order-row-${o.id}`}>
            <td className="px-4 py-3">
              <p className="text-white font-medium">{o.name}</p>
              <p className="font-mono text-[10px] text-gray-500">{o.advertiser}</p>
              <a href={o.target_url} target="_blank" rel="noreferrer"
                className="font-mono text-[10px] text-[#00E5FF] hover:underline inline-flex items-center gap-1 mt-0.5">
                {String(o.target_url).slice(0, 40)} <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </td>
            <td className="px-4 py-3">
              <img src={o.image_url} alt={o.name} className="w-24 h-14 object-cover rounded border border-white/10" />
            </td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">
              {o.placement}
              {o.exclusive && <span className="block text-[#F59E0B] text-[9px] font-bold mt-0.5">24/7 EXCLUSIVE</span>}
            </td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">
              {o.duration_days}d{o.hours_per_day && o.hours_per_day < 24 ? ` · ${String(o.start_hour || 0).padStart(2, "0")}:00+${o.hours_per_day}h` : ""}
              {o.starts_on && <span className="block text-gray-500 text-[9px] mt-0.5">from {o.starts_on}</span>}
            </td>
            <td className="px-4 py-3 font-mono text-xs text-white">${o.price_usd}</td>
            <td className="px-4 py-3"><StatusBadge status={o.paid ? "paid" : "unpaid"} /></td>
            <td className="px-4 py-3 font-mono text-xs text-gray-400">{timeAgo(o.created_at)}</td>
            <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
            <td className="px-4 py-3">
              {["pending_review", "changes_requested"].includes(o.status) ? (
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={() => resolve(o, "approve")} data-testid={`ad-order-approve-${o.id}`}
                    className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  {o.status === "pending_review" && (
                    <Button size="sm" onClick={() => resolve(o, "request_changes")} data-testid={`ad-order-changes-${o.id}`}
                      className="bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 h-8">
                      <PenLine className="h-3.5 w-3.5 mr-1" /> Changes
                    </Button>
                  )}
                  <Button size="sm" onClick={() => resolve(o, "reject")} data-testid={`ad-order-reject-${o.id}`}
                    className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8"
                    title="Final rejection — automatically refunds the advertiser via Stripe">
                    <X className="h-3.5 w-3.5 mr-1" /> Reject &amp; refund
                  </Button>
                </div>
              ) : <span className="font-mono text-[10px] text-gray-600">resolved{o.refund_id ? " · refunded" : ""}</span>}
            </td>
          </tr>
        ))}
      </Table>
      {(orders || []).length === 0 && (
        <p className="text-gray-500 text-sm mt-6 text-center py-8 border border-dashed border-white/10 rounded-lg">
          No ad orders with this status.
        </p>
      )}
    </div>
  );
}
